import React, { useState, useEffect, useRef } from 'react';
import Gradient, { useColorPicker } from 'react-best-gradient-color-picker';
import styles from './GradientEditor.module.css'; // Importa il modulo CSS

export type MapLibreHeatmapColorExpression = (string | number | string[])[];

export interface GradientEditorProps {
  /** Espressione iniziale del colore heatmap di MapLibre. */
  initialExpression: MapLibreHeatmapColorExpression;
  /** Callback chiamata quando l'espressione del colore cambia. */
  onChange: (newExpression: MapLibreHeatmapColorExpression) => void;
  /** Se true, disabilita l'editor. Default: false. */
  isDisabled?: boolean;
  /** Nasconde i campi di input RGBA/HSLA/HEX. Default: false. */
  hideInputs?: boolean;
  /** Nasconde il cursore dell'opacità. Default: false. */
  hideOpacity?: boolean;
  /** Nasconde i controlli (tipo di gradiente, angolo, stop). Default: false. */
  hideControls?: boolean; // Questa è una prop generica, la libreria ha hideGradientControls, hideGradientAngle, hideGradientStop, hideGradientType
  /** Nasconde i pulsanti per cambiare il tipo di colore (Solid/Gradient). Default: false. */
  hideColorTypeBtns?: boolean;
  /** Nasconde i preset di colore. Default: false. */
  hidePresets?: boolean;
  /** Nasconde lo strumento contagocce. Default: false. */
  hideEyeDrop?: boolean;
  /** Nasconde i cursori avanzati (es. per HSL). Default: false. */
  hideAdvancedSliders?: boolean;
  /** Nasconde la guida dei colori. Default: false. */
  hideColorGuide?: boolean;
  /** Nasconde il selettore del tipo di input (HEX, RGBA, etc.). Default: false. */
  hideInputType?: boolean;
  /** Nasconde il selettore del tipo di gradiente (lineare, radiale). Default: false. */
  hideGradientType?: boolean;
  /** Nasconde l'input per l'angolo del gradiente. Default: false. */
  hideGradientAngle?: boolean;
  /** Nasconde l'input per la posizione dello stop del gradiente. Default: false. */
  hideGradientStop?: boolean;
  /** Nasconde i controlli specifici del gradiente (Angle, Stop). Default: false. */
  hideGradientControls?: boolean; // Sembra ridondante con hideGradientAngle e hideGradientStop, ma la aggiungiamo per completezza se la libreria la supporta separatamente.
  /** Array di stringhe di colore per i preset. */
  presets?: string[]; // La libreria si aspetta string[], es. ['rgba(0,0,0,1)', '#fff']
}

const DEFAULT_MAPLIBRE_EXPRESSION: MapLibreHeatmapColorExpression = [
  'interpolate',
  ['linear'],
  ['heatmap-density'],
  0, 'rgba(0,0,255,0)',
  0.1, 'royalblue',
  0.3, 'cyan',
  0.5, 'lime',
  0.7, 'yellow',
  1, 'red',
];

const DEFAULT_CSS_GRADIENT = "linear-gradient(90deg, rgba(0,0,255,0) 0%, royalblue 10%, cyan 30%, lime 50%, yellow 70%, red 100%)";


export const mapLibreToCssGradient = (expression: MapLibreHeatmapColorExpression): string => {
  if (
    !Array.isArray(expression) ||
    expression.length < 5 || // interpolate, linear, heatmap-density, val, color
    expression[0] !== 'interpolate' ||
    !Array.isArray(expression[1]) || expression[1][0] !== 'linear' ||
    !Array.isArray(expression[2]) || expression[2][0] !== 'heatmap-density'
  ) {
    console.warn('Invalid MapLibre expression, returning default CSS gradient:', expression);
    return DEFAULT_CSS_GRADIENT;
  }

  const stops: { density: number; color: string }[] = [];
  for (let i = 3; i < expression.length; i += 2) {
    const density = expression[i];
    const color = expression[i + 1];
    if (typeof density === 'number' && typeof color === 'string') {
      stops.push({ density, color });
    } else {
      console.warn('Invalid stop in MapLibre expression, returning default CSS gradient:', density, color);
      return DEFAULT_CSS_GRADIENT;
    }
  }

  if (stops.length < 2) {
    console.warn('MapLibre expression has less than 2 stops, returning default CSS gradient.');
    return DEFAULT_CSS_GRADIENT;
  }

  // Assicurati che gli stop siano ordinati per densità per una corretta costruzione del gradiente CSS
  stops.sort((a, b) => a.density - b.density);

  const cssStops = stops.map(stop => `${stop.color} ${stop.density * 100}%`).join(', ');
  return `linear-gradient(90deg, ${cssStops})`;
};

export const cssGradientToMapLibre = (gradientObject: any): MapLibreHeatmapColorExpression => {
  if (!gradientObject || !Array.isArray(gradientObject.colors) || gradientObject.colors.length < 2) {
    console.warn('Invalid gradient object or less than 2 color stops, returning default MapLibre expression:', gradientObject);
    return [...DEFAULT_MAPLIBRE_EXPRESSION]; // Restituisce una copia
  }

  const mapLibreStops: (number | string)[] = [];
  // L'array gradientObject.colors è già ordinato dalla libreria
  gradientObject.colors.forEach((stop: { value: string; left: number }) => {
    const density = parseFloat((stop.left / 100).toFixed(4)); // Converte percentuale in 0-1 e arrotonda
    mapLibreStops.push(density, stop.value);
  });

  // Assicurati che gli stop siano ordinati per densità prima di costruire l'espressione
  // Questo è necessario perché l'input `left` potrebbe non essere sempre perfettamente ordinato
  // o potrebbero esserci duplicati che vogliamo gestire consistentemente.
  const structuredStops: { density: number, color: string }[] = [];
  for (let i = 0; i < mapLibreStops.length; i += 2) {
    structuredStops.push({ density: mapLibreStops[i] as number, color: mapLibreStops[i+1] as string });
  }
  structuredStops.sort((a, b) => a.density - b.density);

  const finalMapLibreStops = structuredStops.flatMap(s => [s.density, s.color]);


  return ['interpolate', ['linear'], ['heatmap-density'], ...finalMapLibreStops];
};


const GradientEditor: React.FC<GradientEditorProps> = ({
  initialExpression,
  onChange,
  isDisabled = false,
  hideInputs = false,
  hideOpacity = false,
  hideControls = false, // Questa prop generale potrebbe mappare a più props specifiche della libreria
  hideColorTypeBtns = true,
  hidePresets = true,
  hideEyeDrop = true,
  hideAdvancedSliders = true,
  hideColorGuide = true,
  hideInputType = true,
  hideGradientType = false,
  hideGradientAngle = false,
  hideGradientStop = false,
  hideGradientControls = false, // Specifica della libreria
  presets,
}) => {
  const [pickerValue, setPickerValue] = useState<string>(() => mapLibreToCssGradient(initialExpression));
  const [lastPropagatedMapLibreExpr, setLastPropagatedMapLibreExpr] = useState<MapLibreHeatmapColorExpression | null>(null);
  const { getGradientObject } = useColorPicker(pickerValue, setPickerValue);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(280); // Default width

  useEffect(() => {
    const newPickerValue = mapLibreToCssGradient(initialExpression);
    setPickerValue(newPickerValue);
    setLastPropagatedMapLibreExpr(initialExpression);
  }, [initialExpression]);

  useEffect(() => {
    if (isDisabled) return;

    const gradientObj = getGradientObject(pickerValue);
    if (gradientObj && gradientObj.colors && gradientObj.colors.length > 0) {
      const newMapLibreExpr = cssGradientToMapLibre(gradientObj);
      if (JSON.stringify(newMapLibreExpr) !== JSON.stringify(lastPropagatedMapLibreExpr)) {
        onChange(newMapLibreExpr);
        setLastPropagatedMapLibreExpr(newMapLibreExpr);
      }
    }
  }, [pickerValue, getGradientObject, onChange, isDisabled, lastPropagatedMapLibreExpr]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries && entries.length > 0 && entries[0].contentRect) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []); // Esegui solo al mount e unmount

  // Calcolo dinamico dell'altezza basato sulla larghezza del contenitore.
  // Mantiene l'altezza tra 200px (min) e 250px (max),
  // scalando con la larghezza (containerWidth * 0.6) tra questi limiti.
  // Questo assicura che l'editor non diventi né troppo piccolo né troppo grande verticalmente.
  const calculatedHeight = Math.min(
    250, // Altezza massima
    Math.max(
      200, // Altezza minima
      Math.floor(containerWidth * 0.6)
    )
  );

  return (
    <div
      ref={containerRef}
      className={styles.gradientEditorContainer} // Applica la classe CSS
      style={{
        width: '100%', // Assicura che il div prenda tutta la larghezza disponibile
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? 'none' : 'auto',
    }}>
      {containerWidth > 0 && ( // Renderizza Gradient solo se containerWidth è stato calcolato
        <Gradient
          value={pickerValue}
          onChange={setPickerValue}
          width={Math.max(containerWidth, 150)} // Usa la larghezza del contenitore, con un minimo
          height={calculatedHeight} // Applica l'altezza calcolata dinamicamente
          // Props esposte
          hideInputs={hideInputs}
          hideOpacity={hideOpacity}
          // hideControls è una prop generica del nostro wrapper.
          // La libreria ha props più granulari come hideGradientType, hideGradientAngle, hideGradientStop, hideGradientControls.
          // Se hideControls è true, potremmo voler nascondere tutte queste.
          // Per ora, passiamo hideGradientControls se hideControls è true, altrimenti il valore specifico di hideGradientControls.
          // Questo potrebbe necessitare di una logica più raffinata se hideControls deve sovrascrivere le altre.
          // Per semplicità, diamo precedenza alle props più specifiche se definite, altrimenti a hideControls.
          hideColorTypeBtns={hideColorTypeBtns}
          hidePresets={hidePresets}
          hideEyeDrop={hideEyeDrop}
          hideAdvancedSliders={hideAdvancedSliders}
          hideColorGuide={hideColorGuide}
          hideInputType={hideInputType}
          hideGradientType={hideControls || hideGradientType}
          hideGradientAngle={hideControls || hideGradientAngle}
          hideGradientStop={hideControls || hideGradientStop}
          hideGradientControls={hideControls || hideGradientControls} // Prop specifica della libreria
          presets={presets} // Passa i presets se forniti
        />
      )}
    </div>
  );
};

export default GradientEditor;